import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { authAPI, userAPI } from '../services/api';
import { logger } from '../utils/logger';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  twoFactorEnabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (credentials: any, is2FA?: boolean) => Promise<any>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Setze das Token global für alle API-Anfragen
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const validateToken = async (savedToken: string) => {
    try {
      logger.auth('Validating saved token');
      setToken(savedToken);

      const userProfile = await userAPI.getProfile();
      setUser(userProfile.data);

      logger.auth('Token validation successful');
      return true;
    } catch (error: any) {
      logger.warn('Token validation failed:', error.response?.status);
      setUser(null);
      setToken(null);
      localStorage.removeItem('authToken');
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      logger.component('AuthProvider', 'Checking for saved auth token');
      const savedToken = localStorage.getItem('authToken');

      if (savedToken) {
        const isValid = await validateToken(savedToken);
        if (!isValid) {
          logger.warn('Saved token was invalid, user needs to login again');
        }
      }

      setLoading(false);
      logger.component('AuthProvider', 'Initialization complete');
    };

    initializeAuth();
  }, []);

  const login = async (credentials: any, is2FA = false) => {
    try {
      logger.auth('Login attempt starting');
      const response = is2FA ? await authAPI.verify2FA(credentials) : await authAPI.login(credentials);

      if (response.data.requires2FA) {
        return { requires2FA: true, userId: response.data.userId };
      }

      if (response.data.token) {
        setToken(response.data.token);
        localStorage.setItem('authToken', response.data.token);

        const userProfile = await userAPI.getProfile();
        setUser(userProfile.data);

        logger.auth('Login successful');
        return response.data;
      }

      throw new Error('Ungültige Antwort vom Server');
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    logger.auth('Logging out');
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
  };

  const contextValue = {
    user,
    token,
    login,
    logout,
    loading
  };

  logger.component('AuthProvider', 'Render state', {
    hasUser: !!user,
    hasToken: !!token,
    loading
  });

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};